import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, ArrowRight, User, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, requestPasswordReset } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [isAdminLogin, setIsAdminLogin] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [resetEmail, setResetEmail] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsLoading(true);

    try {
      // Check if this is an admin login attempt
      if (isAdminLogin || formData.email.toLowerCase() === 'ach-admin') {
        // Try admin login
        const response = await axios.post(`${API}/admin/login`, {
          username: formData.email,
          password: formData.password
        });
        const { token, user } = response.data;
        localStorage.setItem('ach_admin_token', token);
        localStorage.setItem('ach_admin_user', JSON.stringify(user));
        toast.success('Welcome, Admin!');
        navigate('/admin/dashboard');
      } else {
        // Regular member login
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
        navigate('/dashboard');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed. Please try again.';
      toast.error(message);
      
      // Check if account is locked
      if (error.response?.status === 403) {
        setShowReset(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetRequest = async (e) => {
    e.preventDefault();
    
    if (!resetEmail) {
      toast.error('Please enter your email');
      return;
    }

    setIsLoading(true);

    try {
      const result = await requestPasswordReset(resetEmail);
      toast.success('Password reset instructions sent to your email');
      // For demo purposes, show the reset token
      if (result.reset_token) {
        toast.info(`Reset token: ${result.reset_token}`, { duration: 10000 });
      }
      setShowReset(false);
    } catch (error) {
      toast.error('Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center bg-brand-surface py-12 px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className={`w-16 h-16 ${isAdminLogin ? 'bg-slate-900' : 'bg-brand-navy'} rounded-full flex items-center justify-center mx-auto mb-4`}>
              {isAdminLogin ? <Shield className="w-8 h-8 text-white" /> : <User className="w-8 h-8 text-white" />}
            </div>
            <h1 className="font-heading font-bold text-2xl text-brand-navy mb-2">
              {showReset ? 'Reset Password' : isAdminLogin ? 'Admin Login' : 'Member Login'}
            </h1>
            <p className="font-body text-brand-charcoal text-sm">
              {showReset 
                ? 'Enter your email to receive reset instructions'
                : isAdminLogin
                ? 'Access the admin portal'
                : 'Sign in to track your requests and message us'
              }
            </p>
          </div>

          {showReset ? (
            // Reset Password Form
            <form onSubmit={handleResetRequest} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="resetEmail" className="font-body text-sm text-brand-charcoal">
                  Email Address
                </Label>
                <Input
                  id="resetEmail"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="form-input-focus font-body"
                  data-testid="reset-email-input"
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-brand-navy hover:bg-brand-action text-white font-body font-medium rounded-full py-6"
                data-testid="reset-submit-btn"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Send Reset Link'
                )}
              </Button>

              <button
                type="button"
                onClick={() => setShowReset(false)}
                className="w-full text-center font-body text-sm text-brand-charcoal hover:text-brand-navy"
              >
                Back to Login
              </button>
            </form>
          ) : (
            // Login Form
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="font-body text-sm text-brand-charcoal">
                  {isAdminLogin ? 'Username' : 'Email Address'}
                </Label>
                <Input
                  id="email"
                  name="email"
                  type={isAdminLogin ? 'text' : 'email'}
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={isAdminLogin ? 'Enter admin username' : 'your@email.com'}
                  className="form-input-focus font-body"
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="font-body text-sm text-brand-charcoal">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    className="form-input-focus font-body pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-charcoal hover:text-brand-navy"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowReset(true)}
                  className="font-body text-sm text-brand-accent hover:text-brand-navy"
                  data-testid="forgot-password-btn"
                >
                  Forgot password?
                </button>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className={`w-full ${isAdminLogin ? 'bg-slate-900 hover:bg-slate-800' : 'bg-brand-navy hover:bg-brand-action'} text-white font-body font-medium rounded-full py-6`}
                data-testid="login-submit-btn"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    {isAdminLogin ? 'Access Admin Portal' : 'Sign In'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          )}

          {/* Footer */}
          {!showReset && (
            <div className="mt-6 space-y-3">
              <div className="text-center">
                <p className="font-body text-sm text-brand-charcoal">
                  Don't have an account?{' '}
                  <Link 
                    to="/register" 
                    className="text-brand-navy font-medium hover:underline"
                    data-testid="register-link"
                  >
                    Sign up free
                  </Link>
                </p>
              </div>
              
              {/* Admin Toggle */}
              <div className="pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => {
                    setIsAdminLogin(!isAdminLogin);
                    setFormData({ email: '', password: '' });
                  }}
                  className="w-full flex items-center justify-center gap-2 font-body text-sm text-brand-charcoal hover:text-brand-navy transition-colors"
                  data-testid="admin-toggle-btn"
                >
                  <Shield className="w-4 h-4" />
                  {isAdminLogin ? 'Switch to Member Login' : 'Admin Login'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
